import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { useHandleRequiredForAIInputBlur } from 'components/BusinessToolForm/hooks/useHandleRequiredForAIInputBlur';
import { ProjectStatus } from 'components/BusinessToolForm/Shared/ProjectStatus';

import { concatWithDot } from 'helpers/string';

import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

export const ProjectOverview = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const handleRequiredForAIInputBlur = useHandleRequiredForAIInputBlur();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.OVERVIEW,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.PROJECT,
  );

  return (
    <Box>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          section={FOR_BANK_SECTIONS.OVERVIEW}
          locked={!canAccess}
          title={t('overviewTitle')}
          badgeText={t('overview.project.title')}
        />
      </FormSectionCard>
      {/* 0.2.1 */}
      <FormSectionCard title={t('overview.project.projectStatus')} disabled={!canAccess}>
        <ProjectStatus canAccess={canAccess} />
      </FormSectionCard>
      {/* 0.2.2 */}
      <FormSectionCard
        title={t('overview.project.goalsAndObjectives')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_GOALS)}
        disabled={!canAccess}
      >
        <TextInputControl
          multiline
          control={control}
          id="overview.company.goalsAndObjectives"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_GOALS)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
        />
      </FormSectionCard>
      {/* 0.2.3 */}
      <FormSectionCard
        title={t('overview.project.briefProjectDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
      >
        <TextInputControl
          multiline
          control={control}
          id="overview.company.briefProjectDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
          onBlur={handleRequiredForAIInputBlur}
        />
      </FormSectionCard>
      {/* 0.2.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('overview.project.coverTitle')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COVER_TITLE)}
      >
        <TextInputControl
          control={control}
          id="overview.project.coverTitle"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COVER_TITLE)}
          disabled={!canAccess}
          maxLength={70}
        />
      </FormSectionCard>
      {/* 0.2.5 */}
      <FormSectionCard
        title={t('overview.project.mainProducts')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES)}
        disabled={!canAccess}
      >
        <TextInputControl
          multiline
          control={control}
          id="overview.company.mainProducts"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_PRODUCTS_OR_SERVICES)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
        />
      </FormSectionCard>
      {/* 0.2.6 */}
      <FormSectionCard
        title={t('overview.project.strategy')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS)}
        disabled={!canAccess}
      >
        <TextInputControl
          multiline
          control={control}
          id="overview.company.strategy"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.STRATEGY_FOR_ACHIEVING_GOALS)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
        />
      </FormSectionCard>
    </Box>
  );
};
